import { useEffect, createContext, useState, useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import cloneDeep from "lodash/cloneDeep";

import {
  updateTemplateHelper,
  getDefaultTemplate as resolveDefaultTemplate,
} from "@/helpers/updateTemplateUtil";
import {
  FONTS,
  TEMPLATE_STYLES,
  LAYOUT_TEMPLATES,
  RECOMMENDED_APPS,
  CLASSIC_LAYOUT_MENU,
  isAppEnabledForPlan,
} from "@/constants/onboarding";
import useUnsplash from "@/hooks/useUnsplash";
import { getSiteplusToken } from "@/api/onboarding";
import getSessionInfo from "@/api/getSessionInfo";
import getAMProductDetails from "@/api/getAMProductDetails";
import initPageViewEvent from "@/trackers/adobe/initPageViewEvent";

import { useTemplateContext } from "./TemplateProvider";
import { getCookieValue } from "@/helpers/index.js";
import { initializePendo } from "@/trackers/pendo/pendoInit.js";
import AuthUjwtHelper from "@/helpers/authUjwtHelper";
import useOnboardingProgress from "@/hooks/useOnboardingProgress";

/** Pages step (`step-8`) toggles these section ids; they map to `RECOMMENDED_APPS` for `buildVisibility` / sitewizard. */
const GRID_PAGES_APP_IDS = ["store", "appointments", "blog"];

// Wrapper component to use useOnboardingProgress inside Router context
const OnboardingContextWrapper = ({ children, value }) => {
  const { markAsComplete, onboardingStateSessionId, clearOnboardingSessionId } =
    useOnboardingProgress(value);

  return (
    <OnboardingContext.Provider
      value={{
        ...value,
        markAsComplete,
        onboardingStateSessionId,
        clearOnboardingSessionId,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};
OnboardingContextWrapper.propTypes = {
  children: PropTypes.node,
  value: PropTypes.object,
};

import {
  updatePalette,
  updateFont,
  updateHeaderLogo,
} from "@/helpers/updateTemplateUtil.js";
import {
  keepSessionAlive,
  sfCoreRefreshCall,
} from "@/helpers/sessionHelper.js";
import { initializeFullstory } from "@/trackers/fullstory/fullstoryInit.js";
import { getGridLayout, getTokenForProjects } from "../api/onboarding";
import { TEMPLATE_VERSIONS } from "../constants/versions";
import { useOnboardingStatusFromStoredash } from "../hooks/useOnboarding";
import { useGettingStartedDomainForm } from "@/hooks/useGettingStartedDomainForm";
import { TONES } from "../constants/onboarding";
import {
  readGridOnboardingSession,
  clearGridOnboardingSession,
} from "@/helpers/gridOnboardingSessionHelper";

export const OnboardingContext = createContext();

/**
 * This component handles various steps in the template customization flow.
 *
 * States:
 * - actualStep: Tracks the current main step in the flow (initialized to 0).
 * - actualSubStep: Tracks the current sub-step in the flow (initialized to 0).
 * - templateStyle: Stores the current template style (defaulted to TEMPLATE_STYLES.PLAYFUL).
 * - selectedFont: Stores the font selected by the user for customization.
 * - selectedLayout: Tracks the selected layout, initialized to the first layout option.
 * - customFont: Stores any custom fonts provided by the user.
 * - ujwt: Stores the user's JWT token, if available.
 * - contentStructureData: Contains content-related structure data for the template.
 * - templateCategoryData: Stores category-related data for the template.
 * - websiteDetails: title / category / prompt for getting-started; when a resolvable domain + SFAM GET exist, `title`↔`businessName`, `category`↔`businessDescription`, `prompt`↔`businessUnique` in domain form `formData`.
 * - selectedPalette: Stores the color palette selected by the user.
 * - selectedTemplate: Tracks the selected template by the user.
 * - startingTemplate: Stores the initially chosen template.
 * - logoImage: Stores the uploaded logo image file.
 * - coverImage: Represents the current cover image applied to the template (changes based on user selection or uploads).
 *
 * Hooks:
 * - useUnsplash: Manages querying Unsplash for default cover images and updates.
 * - useTemplateContext: Provides functions for setting and safely updating the template.
 *
 * Memoized Values:
 * - layoutOptions: Dynamically generates a list of layout options based on the selected starting template,
 *   filtering out the current starting template and including up to 5 additional options from LAYOUT_TEMPLATES.
 */

const DEFAULT_TEMPLATES_VERSION = "v2";

const OnboardingContextProvider = ({ children }) => {
  const [exit, setExit] = useState(false);
  const [allowRender, setAllowRender] = useState(false);
  const [maxStep, setMaxStep] = useState(0);
  const [actualStep, setActualStep] = useState(0);
  const [actualSubStep, setActualSubStep] = useState(0);
  const [templateStyle, setTemplateStyle] = useState(TEMPLATE_STYLES.PLAYFUL);
  const [selectedFont, setSelectedFont] = useState();
  const [selectedLayout, setSelectedLayout] = useState(0);
  const [customFont, setCustomFont] = useState();
  const [ujwt, setUjwt] = useState("");
  const [isGrid, setIsGrid] = useState(null);
  const [token, setToken] = useState(null);
  const [contentStructureData, setContentStructureData] = useState();
  const [templateCategoryData, setTemplateCategoryData] = useState();
  const [websiteDetails, setWebsiteDetails] = useState({
    title: "",
    category: "",
    prompt: "",
  });
  const [selectedPalette, setSelectedPalette] = useState();
  const [templateButtonStyle, setTemplateButtonStyle] = useState("default");
  const [selectedTemplate, setSelectedTemplate] = useState();
  const [startingTemplate, setStartingTemplate] = useState();
  const [productInstanceId, setProductInstanceId] = useState();
  const [accessToken, setAccessToken] = useState();
  const [sections, setSections] = useState({
    header_and_introduction: true,
    contact_form: false,
    testimonials: false,
    about: false,
    footer: true,
    products: false,
    services: false,
  });
  const [siteplusLoginUrl, setSiteplusLoginUrl] = useState();
  const [logoImage, setLogoImage] = useState();
  const [coverImage, setCoverImage] = useState();
  const { setUnsplashQuery, defaultCoverImages, setDefaultCoverImages } =
    useUnsplash();
  const { safeSetTemplate, setTemplate } = useTemplateContext();
  const [stepSequence, setStepSequence] = useState();
  const [isRecreating, setIsRecreating] = useState(false);
  const [defaultTemplatesVersion, setDefaultTemplatesVersion] = useState(
    DEFAULT_TEMPLATES_VERSION,
  );
  const [recommendedApps, setRecommendedApps] = useState(RECOMMENDED_APPS);
  const [appsSetupData, setAppsSetupData] = useState({});
  const [hasCompletedOboardingStep, setHasCompletedOnboardingStep] =
    useState(false);
  const [previousAppChoices, setPreviousAppChoices] = useState({});
  const [tonesData, fillTonesData] = useState();
  const [selectedTone, setTone] = useState();
  const [rawTonesData, setRawTonesData] = useState();
  const [isGridLoaded, setIsGridLoaded] = useState(false);
  const [sku, setSku] = useState();
  const [layout, setLayout] = useState(0);
  const [classicMenu, setClassicMenu] = useState(CLASSIC_LAYOUT_MENU);
  const [currentProjectId, setCurrentProjectId] = useState();
  const [sortedSections, setSortedSections] = useState();
  const [checkedSortedSections, setCheckedSortedSections] = useState();
  const [page, setPage] = useState(0);

  const [gridSitewizardId, setGridSitewizardId] = useState(null);
  const [gridAiPageData, setGridAiPageData] = useState({});
  const [lastGridGeneratedTemplateId, setLastGridGeneratedTemplateId] =
    useState(null);
  const [gridGenerationStatus, setGridGenerationStatus] = useState("idle");
  const pageViewTrackedKeyRef = useRef(null);

  const clearGridOnboardingState = useCallback(() => {
    clearGridOnboardingSession(productInstanceId);
    setGridSitewizardId(null);
    setGridAiPageData({});
    setLastGridGeneratedTemplateId(null);
    setGridGenerationStatus("idle");
  }, [productInstanceId]);

  useEffect(() => {
    if (!isGrid || !productInstanceId) return;
    const saved = readGridOnboardingSession(productInstanceId);
    if (!saved) return;
    if (saved.sitewizardId) setGridSitewizardId(saved.sitewizardId);
    if (saved.lastGeneratedTemplateId) {
      setLastGridGeneratedTemplateId(saved.lastGeneratedTemplateId);
    }
    if (
      saved.aiPageData &&
      typeof saved.aiPageData === "object" &&
      Object.keys(saved.aiPageData).length > 0
    ) {
      setGridAiPageData(saved.aiPageData);
      setGridGenerationStatus("ready");
    }
  }, [isGrid, productInstanceId]);

  const {
    data: storeOnboardingData,
    isFetching: fetchingStoreOnboarding,
    isFetched: fetchedStoreOnboarding,
    refetch: refetchStoreOnboarding,
  } = useOnboardingStatusFromStoredash(productInstanceId);

  const {
    domainFormLookup,
    domainFormInfoQuery,
    hasResolvableDomainForm,
    domainFormPersistReady,
    persistDomainFormAfterGettingStarted,
  } = useGettingStartedDomainForm({
    projectsToken: token,
    productInstanceId,
    setWebsiteDetails,
  });

  const layoutOptions = useMemo(() => {
    const selectedLayout = LAYOUT_TEMPLATES.find(
      ({ id }) => id === startingTemplate?.id,
    );
    const layouts = [
      ...LAYOUT_TEMPLATES.filter(
        (layout) => layout.id !== startingTemplate?.id,
      ).slice(0, selectedLayout ? 5 : 6),
    ];

    if (selectedLayout) {
      return [selectedLayout, ...layouts];
    }

    return layouts;
  }, [startingTemplate]);

  const getDefaultTemplate = (templateStyle) => {
    const templateVersions = TEMPLATE_VERSIONS();
    const templateStyleKeys = Object.keys(templateVersions);
    const templateStyleKey = templateStyleKeys.includes(templateStyle)
      ? templateStyle
      : "playful";
    const templateVersion = ["v2", "v1"].includes(defaultTemplatesVersion)
      ? defaultTemplatesVersion
      : DEFAULT_TEMPLATES_VERSION;

    return resolveDefaultTemplate(templateStyleKey, templateVersion);
  };

  const getDefaultSwatch = (
    templateStyle,
    templateVersion = defaultTemplatesVersion,
  ) => {
    const templateVersions = TEMPLATE_VERSIONS();
    const templateStyleKeys = Object.keys(templateVersions);
    const templateStyleKey = templateStyleKeys.includes(templateStyle)
      ? templateStyle
      : "playful";
    const version = ["v2", "v1"].includes(templateVersion)
      ? templateVersion
      : DEFAULT_TEMPLATES_VERSION;

    return templateVersions[templateStyleKey][version].defaultSwatch;
  };

  const setTonesData = (data) => {
    if (!data) return;
    const updatedTones = cloneDeep(TONES);
    updatedTones.forEach((tone) => {
      if (!(tone.label === "custom")) {
        tone.headline = data.tones[tone.label].title;
        tone.subtitle = data.tones[tone.label].shortDescription;
        tone.buttonText = data.tones[tone.label].button1text;
      }
    });
    fillTonesData(updatedTones);
  };

  const updateContent = () => {
    try {
      if (!websiteDetails.title || isEmpty(contentStructureData)) return;
      updateTemplateHelper(safeSetTemplate, {
        "logo.text": websiteDetails.title,
        "sections.hero.title": contentStructureData.blocks.cover.title.content,
        "sections.hero.subtitle":
          contentStructureData.blocks.cover.shortDescription.content,
        "sections.hero.subtitle2":
          contentStructureData.blocks.cover.subtitle.content,
        "sections.hero.ctaText":
          contentStructureData.blocks.cover.button1text.content,
        "sections.header.title":
          contentStructureData.blocks.header.title.content,
        "sections.features": Object.keys(
          contentStructureData.blocks.features,
        ).map((feature) => ({
          title: contentStructureData.blocks.features[feature].content,
        })),
      });
    } catch (e) {
      console.error("Error updating content", e);
    }
  };

  const checkGridLayout = async (token) => {
    const res = await getGridLayout(token);
    const grid = res?.grid;
    setIsGrid(grid);
    if (!grid) {
      setDefaultTemplatesVersion("v1");
      setSections((prev) => ({
        ...prev,
        about: true,
        contact_form: true,
        testimonials: true,
      }));
    }
    setIsGridLoaded(true);
  };

  const resetImages = () => {
    setLogoImage();
    setCoverImage();
    updateTemplateHelper(safeSetTemplate, {
      "logo.src": "",
      "sections.hero.backgroudnImage": "",
    });
  };

  const getDefaultCoverImage = () => {
    return defaultCoverImages?.landscape;
  };

  const setNewCoverImage = (template) => {
    const newCoverImage = getDefaultCoverImage(
      template.initialState.sections.hero.orientation,
    );
    safeSetTemplate("sections.hero.backgroundImage", newCoverImage?.url ?? "");
    setCoverImage(newCoverImage);
  };

  const initAuth = () => {
    const pId = getCookieValue("_sp_productInstanceId");
    const aToken = getCookieValue("_sp_accessToken");
    if (isNil(window.sessionStorage.getItem(`templates_created_${pId}`))) {
      window.sessionStorage.setItem(`templates_created_${pId}`, 0);
    }

    setProductInstanceId(pId);
    setAccessToken(aToken);

    AuthUjwtHelper.setProductInstanceId(pId);
    AuthUjwtHelper.setRefreshCallback(getSiteplusToken);
    AuthUjwtHelper.setOnTokenRefresh(setUjwt);
  };

  useEffect(() => {
    const apps = JSON.parse(storeOnboardingData?.appChoices || "{}");
    const planSku = storeOnboardingData?.planSku;
    setRecommendedApps((prev) =>
      prev.map((app) => {
        // Hard gate: a plan-disabled app can never be enabled, regardless of stale `appChoices`.
        if (!isAppEnabledForPlan(app.id, planSku)) {
          return { ...app, enabled: false };
        }
        if (isGrid === true && GRID_PAGES_APP_IDS.includes(app.id)) {
          return app;
        }
        return { ...app, enabled: apps[app.id] ?? false };
      }),
    );
  }, [storeOnboardingData, isGrid]);

  /** Grid: store / appointments / blog come from Pages checkboxes — keep `recommendedApps` in sync for TemplateProvider `buildVisibility`. */
  useEffect(() => {
    if (isGrid !== true) return;
    const planSku = storeOnboardingData?.planSku;

    setRecommendedApps((prev) =>
      prev.map((app) => {
        if (!GRID_PAGES_APP_IDS.includes(app.id)) return app;
        // Same hard gate: never enable a plan-disabled app even if `sections` or
        // `checkedSortedSections` was hydrated with a stale truthy value.
        if (!isAppEnabledForPlan(app.id, planSku)) {
          return { ...app, enabled: false };
        }

        const row = checkedSortedSections?.find((s) => s.id === app.id);
        if (row != null && typeof row.checked === "boolean") {
          return { ...app, enabled: row.checked };
        }
        if (
          sections != null &&
          Object.prototype.hasOwnProperty.call(sections, app.id)
        ) {
          return { ...app, enabled: Boolean(sections[app.id]) };
        }
        return app;
      }),
    );
  }, [isGrid, checkedSortedSections, sections, storeOnboardingData]);

  useEffect(() => {
    if (!isNil(accessToken)) {
      getTokenForProjects(accessToken).then((res) => {
        setToken(res.token);
      });
    }
  }, [accessToken]);

  useEffect(() => {
    if (!isNil(token)) {
      checkGridLayout(token);
    }
  }, [token]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const allow = urlParams.get("allow");
    if (window.location.href.includes("form") && !allow) {
      window.location.href = "/";
    }
    setAllowRender(true);
    initializeFullstory();
  }, []);

  useEffect(() => {
    const productId =
      productInstanceId || getCookieValue("_sp_productInstanceId") || "";
    const trackKey = productId || "__no_product__";

    if (pageViewTrackedKeyRef.current === trackKey) {
      return;
    }

    pageViewTrackedKeyRef.current = trackKey;

    const trackPageView = async () => {
      try {
        const [sessionInfo, productDetails] = await Promise.all([
          getSessionInfo(),
          productId
            ? getAMProductDetails({ productInstanceId: productId })
            : Promise.resolve(null),
        ]);

        await initPageViewEvent({
          productInstanceId: productId,
          siteEditorTitle: document.title,
          sessionInfo,
          productDetails,
        });
      } catch (error) {
        pageViewTrackedKeyRef.current = null;
        console.error("Adobe page view tracking failed:", error);
      }
    };

    trackPageView();
  }, [productInstanceId]);

  useEffect(() => {
    const intervalId1 = setInterval(keepSessionAlive, 5 * 60 * 1000); // Refresh every 5 minutes
    const intervalId2 = setInterval(sfCoreRefreshCall, 5 * 60 * 1000); // Refresh every 5 minutes
    return () => {
      clearInterval(intervalId1);
      clearInterval(intervalId2);
    };
  }, []);

  useEffect(() => {
    if (!isEmpty(ujwt)) {
      initializePendo(ujwt);
    }
  }, [ujwt]);

  useEffect(() => {
    // TDOD: Add a redirect to get started page if the websiteDetails is empty
    initAuth();
  }, []);

  useEffect(() => {
    if (isEmpty(templateCategoryData)) return;
    setUnsplashQuery({
      query: templateCategoryData.photoCategory,
      step: 0,
      page: 1,
      unique: new Date().getTime(),
    });
  }, [templateCategoryData]);

  useEffect(() => {
    updateContent();
  }, [contentStructureData]);

  useEffect(() => {
    if (!selectedTemplate) return;

    const alreadyAdded = document.querySelector(
      `link[data-template="${templateStyle}"]`,
    );
    if (!alreadyAdded) {
      let href = "https://fonts.googleapis.com/css2?";
      const fonts = FONTS[templateStyle]?.[selectedTemplate?.version];

      fonts?.forEach((font) => {
        href += `family=${font.header
          .split(",")[0]
          .replace(" ", "+")}:wght@400;700;800&family=${font.body
          .split(",")[0]
          .replace(" ", "+")}:wght@400;700;800&`;
      });

      href = href.slice(0, -1);

      const link = document.createElement("link");
      link.setAttribute("data-template", templateStyle);
      link.setAttribute("rel", "stylesheet");
      link.setAttribute("type", "text/css");
      link.setAttribute("href", href);
      document.head.appendChild(link);
    }
  }, [selectedTemplate]);

  useEffect(() => {
    const template = getDefaultTemplate(templateStyle);
    setStartingTemplate(template);
    setSelectedTemplate(template);
    setSelectedLayout(0);
    setLayout(0);
  }, [templateStyle, defaultTemplatesVersion]);

  useEffect(() => {
    const template = selectedTemplate;
    if (!template) return;

    const templateVersion = template.version;

    const styleDefaultSwatch = getDefaultSwatch(templateStyle, templateVersion);
    const defaultFont = FONTS[templateStyle][templateVersion][0];

    setTemplate((prev) => ({
      ...template.initialState,
      theme: {
        buttonFont: template.initialState.buttonFont,
        buttonStyle: template.initialState.buttonStyle,
        palette: {
          primary: {
            main: styleDefaultSwatch.primary,
          },
          secondary: {
            main: styleDefaultSwatch.secondary,
          },
          text: {
            primary: styleDefaultSwatch.textPrimary,
            secondary: styleDefaultSwatch.textSecondary,
          },
          background: {
            paper: styleDefaultSwatch.complementary,
          },
        },
        typography: {
          fontFamily: defaultFont?.header, // Primary font
          secondaryFont: {
            fontFamily: defaultFont?.body, // Secondary font
          },
          h1: {
            fontFamily: defaultFont?.header,
          },
          h2: {
            fontFamily: defaultFont?.header,
          },
          body1: {
            fontFamily: defaultFont?.body,
          },
        },
      },
      logo: {
        ...(template?.initialState?.logo || {}),
        fontFamily: defaultFont?.body,
      },
      sections: {
        header: {
          ...(prev?.sections?.header || {}),
          ...(template?.initialState?.sections?.header || {}),
          logo: {
            ...(template?.initialState?.sections?.header?.logo || {}),
            fontFamily: defaultFont?.body,
          },
        },
        ...template.initialState.sections,
        hero: {
          ...(template?.initialState?.sections?.hero || {}),
          backgroundImage:
            coverImage?.url ?? getDefaultCoverImage()?.newCoverImage?.url ?? "",
        },
      },
    }));

    updateContent();
  }, [selectedTemplate]);

  useEffect(() => {
    if (
      !isEmpty(defaultCoverImages) &&
      !isEmpty(selectedTemplate) &&
      isEmpty(coverImage)
    ) {
      resetImages();
      setNewCoverImage(selectedTemplate);
    }
  }, [defaultCoverImages]);

  useEffect(() => {
    safeSetTemplate("sections.hero.backgroundImage", coverImage?.url ?? "");
  }, [coverImage]);

  // useEffect(() => {
  //   const templates = flatten(Object.values(templateConfig));
  //   const template = templates.find((t) => t?.id === layoutOptions[selectedLayout]?.id)
  //   setSelectedTemplate(template);

  //   safeSetTemplate('fields', template?.initialState?.fields)
  //   safeSetTemplate('sections.navigation', template?.initialState?.sections.navigation)

  // }, [selectedLayout]);

  useEffect(() => {
    setTemplateButtonStyle(
      selectedTemplate?.initialState?.buttonStyle ?? "default",
    );
  }, [selectedTemplate]);

  useEffect(() => {
    setMaxStep(Math.max(maxStep, actualStep));
  }, [actualStep]);

  useEffect(() => {
    if (!selectedPalette) return;
    updatePalette(selectedPalette, safeSetTemplate);
  }, [selectedPalette]);

  useEffect(() => {
    if (!selectedFont) return;
    updateFont(selectedFont, safeSetTemplate);
  }, [selectedFont]);

  useEffect(() => {
    updateHeaderLogo(logoImage, safeSetTemplate);
  }, [logoImage]);

  const value = {
    layoutOptions,
    ujwt,
    actualStep,
    actualSubStep,
    templateStyle,
    selectedLayout,
    startingTemplate,
    selectedFont,
    customFont,
    selectedTemplate,
    templateButtonStyle,
    sections,
    setSections,
    setSelectedTemplate,
    setActualStep,
    setActualSubStep,
    setCustomFont,
    setSelectedFont,
    setSelectedLayout,
    setTemplateStyle,
    contentStructureData,
    setContentStructureData,
    templateCategoryData,
    setTemplateCategoryData,
    websiteDetails: {
      data: websiteDetails,
      set: (updatedFields) => {
        setWebsiteDetails((prev) => ({
          ...prev,
          ...updatedFields,
        }));
      },
    },
    selectedPalette,
    setSelectedPalette,
    logoImage: {
      url: logoImage,
      set: setLogoImage,
    },
    coverImage: {
      data: coverImage,
      set: setCoverImage,
    },
    productInstanceId,
    accessToken,
    defaultCoverImages: getDefaultCoverImage,
    maxStep,
    siteplusLoginUrl,
    setSiteplusLoginUrl,
    exit,
    setExit,
    setTemplate,
    updateContent,
    resetImages,
    setNewCoverImage,
    stepSequence,
    setStepSequence,
    isGrid,
    setDefaultCoverImages,
    isRecreating,
    setIsRecreating,
    defaultTemplatesVersion,
    setDefaultTemplatesVersion,
    getDefaultTemplate,
    getDefaultSwatch,
    recommendedApps,
    setRecommendedApps,
    appsSetupData,
    setAppsSetupData,
    hasCompletedOboardingStep,
    setHasCompletedOnboardingStep,
    previousAppChoices,
    setPreviousAppChoices,
    storeOnboardingData,
    fetchingStoreOnboarding,
    fetchedStoreOnboarding,
    refetchStoreOnboarding,
    domainFormLookup,
    domainFormInfoQuery,
    hasResolvableDomainForm,
    domainFormPersistReady,
    persistDomainFormAfterGettingStarted,
    tonesData,
    fillTonesData,
    setTonesData,
    selectedTone,
    setTone,
    rawTonesData,
    setRawTonesData,
    sku,
    setSku,
    layout,
    setLayout,
    classicMenu,
    setClassicMenu,
    currentProjectId,
    setCurrentProjectId,
    sortedSections,
    setSortedSections,
    checkedSortedSections,
    setCheckedSortedSections,
    page,
    setPage,
    gridSitewizardId,
    setGridSitewizardId,
    gridAiPageData,
    setGridAiPageData,
    lastGridGeneratedTemplateId,
    setLastGridGeneratedTemplateId,
    gridGenerationStatus,
    setGridGenerationStatus,
    clearGridOnboardingState,
  };

  if (!allowRender || !isGridLoaded) return null;

  return (
    <OnboardingContextWrapper value={value}>
      {children}
    </OnboardingContextWrapper>
  );
};

OnboardingContextWrapper.propTypes = {
  children: PropTypes.node,
  value: PropTypes.object,
};

OnboardingContextProvider.propTypes = {
  children: PropTypes.node,
};
export default OnboardingContextProvider;
